/**
 * Whether this tab is running a bundle the server has since replaced.
 *
 * The app is one document now, so a tab can sit on a build for days: nothing fetches a new
 * one until the user reloads by hand. Two things go wrong with that. The code talks to APIs
 * that have moved on, and - the visible one - an async chunk it has not needed yet may
 * already have been swept out of staticfiles by `prune_bundles`, which leaves a route that
 * can never render.
 *
 * So every response carries the build the server is on (X-Build-Id, from
 * core/middlewares/BuildIdMiddleware.py) and the document carries the one it was rendered
 * with. When they differ the next navigation goes through the server instead of through the
 * router: from where the user sits that is an ordinary page transition, and nothing is
 * interrupted the way a forced reload would interrupt it.
 *
 * Nothing here runs when either id is missing - a checkout that was never built, the dev
 * server, an anonymous page. No id, no check.
 */
import globalEventBus, { GLOBAL_EVENTS } from './globalEventBus';

const RELOAD_GUARD_KEY = 'mm-stale-build-reloaded-at';
const RELOAD_GUARD_SECONDS = 60;

let stale = false;

function pageBuildId () {
    return typeof window.$build_id === 'string' ? window.$build_id : '';
}

/**
 * During a rolling reload the workers disagree for a minute or two, so this compares the ids
 * rather than just checking they differ: a page that has already moved onto the new build
 * must not be sent back by an old worker still answering with the previous one. The ids sort
 * chronologically - see BuildIdPlugin in rspack.config.mjs.
 */
export function noteServerBuildId (serverBuildId) {
    if (stale || !serverBuildId) {
        return;
    }
    const pageId = pageBuildId();
    if (!pageId || serverBuildId <= pageId) {
        return;
    }
    stale = true;
    globalEventBus.emit(GLOBAL_EVENTS.BUILD_IS_STALE, serverBuildId);
}

export function isBuildStale () {
    return stale;
}

function recentlyReloaded () {
    try {
        const at = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY));
        return Boolean(at) && (Date.now() - at) < RELOAD_GUARD_SECONDS * 1000;
    } catch (e) {
        // private windows and blocked site data throw on access; without the guard the worst
        // case is one extra page load, which is better than refusing to recover at all
        return false;
    }
}

/**
 * Leaves the app and lets the server render `link`.
 *
 * Guarded, because both callers fire on a condition the new document may still meet - an old
 * worker answering mid-deploy, a chunk that is missing for good - and a page that reloads
 * itself in a loop is worse than a stale one.
 *
 * @returns {boolean} whether it is leaving
 */
export function reloadInto (link) {
    if (recentlyReloaded()) {
        return false;
    }
    try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch (e) {
        // see recentlyReloaded
    }
    window.location.href = link;
    return true;
}

/** A chunk of this build that is not on the server any more. */
export function isChunkLoadError (error) {
    if (!error) {
        return false;
    }
    if (error.name === 'ChunkLoadError') {
        return true;
    }
    return /Loading (CSS )?chunk .+ failed/i.test(error.message || '');
}
